import type { FC, MouseEvent } from 'react';
import { useCallback, useState } from 'react';
import useEnqueueSnackbar from '../../../hooks/useEnqueueSnackbar';
import copy from 'copy-to-clipboard';
import { Box, Button, IconButton, Menu, MenuItem, ListItemIcon, ListItemText } from '@mui/material';
import CopyAllOutlined from '@mui/icons-material/CopyAllOutlined';
import LaunchOutlined from '@mui/icons-material/LaunchOutlined';
import Facebook from '@mui/icons-material/Facebook';
import MoreVert from '@mui/icons-material/MoreVert';
import PreviewOutlined from '@mui/icons-material/PreviewOutlined';
import type { FacebookCreativeAdRenderEntry } from '../api/types';

type IProps = Pick<
  FacebookCreativeAdRenderEntry,
  'ad_creative_type' | 'ad_id' | 'original_image_url' | 'video_sd_url' | 'video_preview_image_url' | 'ad_library_url'
> & {
  position?: 'top' | 'bottom';
};

const btnWhite = { color: 'white', fontSize: 10 } as const;
const iconSx = { mr: -0.5 } as const;
const iconBtnSx = { color: 'white', p: 0.75 } as const;

const ContentPreviewButtons: FC<IProps> = ({
  ad_creative_type,
  ad_id,
  ad_library_url,
  video_sd_url,
  original_image_url,
  video_preview_image_url,
  position = 'bottom',
}) => {
  const { onShowAlert } = useEnqueueSnackbar();
  const contentLink = ad_creative_type === 'video' ? video_sd_url : original_image_url;
  const previewLink = ad_creative_type === 'video' ? video_preview_image_url : original_image_url;

  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);

  const handleTogglePanelVisibility = useCallback(() => {
    const dialogContainer = document.querySelector(`#creative-${ad_id}-dialog`);
    if (dialogContainer) {
      dialogContainer.classList.toggle('visibility');
      return;
    }

    const paperContainer = document.querySelector(`#creative-${ad_id}-paper`);
    paperContainer?.classList.toggle('visibility');
  }, [ad_id]);

  const handleCopyCreoUrl = useCallback(() => {
    copy(contentLink);
    onShowAlert(Number(ad_id), 'Creo URL has been copied to clipboard!');
  }, [ad_id, contentLink, onShowAlert]);

  const handleCopyPreviewUrl = useCallback(() => {
    copy(previewLink);
    onShowAlert(Number(ad_id), 'Preview URL has been copied to clipboard!');
    handleTogglePanelVisibility();
  }, [previewLink, onShowAlert, ad_id, handleTogglePanelVisibility]);

  const handleOpenUrl = () => {
    window.open(contentLink, '_blank');
  };

  const handleOpenFb = () => {
    window.open(ad_library_url, '_blank');
  };

  const handleMenuOpen = (e: MouseEvent<HTMLElement>) => {
    e.stopPropagation();
    setMenuAnchor(e.currentTarget);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
  };

  return (
    <>
      <Box
        className={'creative-nav-panel'}
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          px: 1,
          position: 'absolute',
          ...(position === 'bottom' ? { bottom: 6 } : { top: 6 }),
          left: 6,
          right: 6,
          height: 42,
          margin: 'auto',
          zIndex: 2,
          backgroundColor: 'rgba(0,0,0, 0.5)',
          borderRadius: '4px',
        }}
      >
        <Button
          onClick={handleCopyCreoUrl}
          variant={'text'}
          size={'small'}
          startIcon={<CopyAllOutlined sx={iconSx} />}
          sx={{ ...btnWhite, lineHeight: '1', textAlign: 'left' }}
        >
          Creo url
        </Button>
        <Button
          onClick={handleOpenUrl}
          variant={'text'}
          size={'small'}
          startIcon={<LaunchOutlined sx={iconSx} />}
          sx={btnWhite}
        >
          Open
        </Button>
        <Button
          onClick={handleOpenFb}
          variant={'text'}
          size={'small'}
          startIcon={<Facebook sx={iconSx} />}
          sx={btnWhite}
        >
          FB
        </Button>
        <IconButton onClick={handleMenuOpen} size="small" sx={iconBtnSx}>
          <MoreVert fontSize="small" />
        </IconButton>
      </Box>
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={handleMenuClose}
        anchorOrigin={{ vertical: position === 'bottom' ? 'top' : 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: position === 'bottom' ? 'bottom' : 'top', horizontal: 'right' }}
      >
        <MenuItem
          onClick={() => {
            handleCopyPreviewUrl();
            handleMenuClose();
          }}
        >
          <ListItemIcon>
            <PreviewOutlined fontSize="small" />
          </ListItemIcon>
          <ListItemText>Copy Preview URL</ListItemText>
        </MenuItem>
      </Menu>
    </>
  );
};

export default ContentPreviewButtons;
